// A location's records (records.forLocation, BL-010/BL-011): notes and alerts, the preferred
// technician, and installed equipment with its warranties; the words the customer record and
// booking use, and the checks before records.addWarranty. Who edits mirrors packages/auth's
// roles.ts: customers.edit is owner, manager, dispatcher and CSR.

/** customers.edit: records.addNote, pinNote, setPreferredTech, addWarranty, voidWarranty. */
export const RECORD_EDIT_PERMS = ['customers.edit'];

export interface RecordNote {
  id: string;
  on: 'customer' | 'location';
  body: string;
  pinned: boolean;
  notedAt: string;
  author: string | null;
  /** Came over from ServiceTitan (records.forLocation): its pin is ServiceTitan's while mirroring. */
  fromServiceTitan?: boolean;
}
export interface Warranty {
  id: string;
  kind: 'manufacturer' | 'labour';
  startsOn: string;
  endsOn: string | null;
  lifetime: boolean;
  inForce: boolean;
  terms: string | null;
  voided: { at: string; reason: string } | null;
}
export interface Equipment {
  id: string;
  kind: string;
  make: string | null;
  model: string | null;
  serial: string | null;
  installedOn: string | null;
  installJobId: string | null;
  /** Taken out of service (equipment.retire): kept on record, its warranties no longer count. */
  retiredAt: string | null;
  retiredReason: string | null;
  installedBy: string | null;
  warranties: Warranty[];
  underWarranty: boolean;
}
export interface LocationRecords {
  notes: RecordNote[];
  preferredTechnician: { staffId: string; name: string } | null;
  contacts: { contactId: string; name: string; relationship: string | null }[];
  equipment: Equipment[];
}

const fmt = (d: string, opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-CA', { ...opts, timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
/** "Mar 2027". */
export const monthYear = (d: string) => fmt(d, { month: 'short', year: 'numeric' });
/** "Mar 10, 2027". */
export const longDay = (d: string) => fmt(d, { month: 'short', day: 'numeric', year: 'numeric' });

/** "the Rheem water heater", "the backflow preventer". */
export const equipmentLabel = (e: Pick<Equipment, 'kind' | 'make'>) =>
  `the ${e.make ? `${e.make} ` : ''}${e.kind.toLowerCase()}`;

/**
 * Booking's line for one piece of equipment under a warranty in force (jobs.book's warranty check):
 * "Under warranty until Mar 2027: the water heater, installed by <the company>." Null when none is.
 */
export function warrantyLine(e: Equipment): string | null {
  const live = e.warranties.filter((w) => w.inForce);
  if (!live.length) return null;
  const by = e.installedBy ? `, installed by ${e.installedBy}` : '';
  if (live.some((w) => w.lifetime)) return `Under a lifetime warranty: ${equipmentLabel(e)}${by}.`;
  const until = live
    .map((w) => w.endsOn!)
    .sort()
    .at(-1)!;
  return `Under warranty until ${monthYear(until)}: ${equipmentLabel(e)}${by}.`;
}

/** "Manufacturer, Mar 10, 2024 to Mar 10, 2027" / "Labour, from Mar 10, 2024, lifetime". */
export function warrantyText(w: Warranty): string {
  const kind = w.kind === 'manufacturer' ? 'Manufacturer' : 'Labour';
  const span = w.endsOn
    ? `${longDay(w.startsOn)} to ${longDay(w.endsOn)}`
    : `from ${longDay(w.startsOn)}, lifetime`;
  return `${kind}, ${span}`;
}
export const warrantyState = (w: Warranty, today: string) =>
  w.voided ? 'Voided' : w.inForce ? 'In force' : w.startsOn > today ? 'Not started' : 'Ended';

/**
 * Which of an address's notes a card shows (history fix 1): all of them where the customer has one
 * address; else the customer's own once, on a card of their own ('customer'), and each address's
 * under it alone ('location'), so a customer with 54 addresses doesn't show their notes 54 times.
 */
export type NotesPart = 'all' | 'customer' | 'location';
const inPart = (n: RecordNote, part: NotesPart) => part === 'all' || n.on === part;

/** Pinned alerts first (as the server orders them), then the rest. */
export const alertsOf = (r: LocationRecords, part: NotesPart = 'all') =>
  r.notes.filter((n) => n.pinned && inPart(n, part));
export const notesOf = (r: LocationRecords, part: NotesPart = 'all') =>
  r.notes.filter((n) => !n.pinned && inPart(n, part));

/**
 * Whether the office pins or unpins this note here (records.pinNote): one added in Flowology always;
 * one that came over from ServiceTitan only once the company is live (BL-088).
 */
export const notePinnable = (
  n: Pick<RecordNote, 'fromServiceTitan'>,
  canEdit: boolean,
  mirroring: boolean,
): boolean => canEdit && !(mirroring && n.fromServiceTitan === true);

export interface WarrantyForm {
  kind: 'manufacturer' | 'labour';
  startsOn: string;
  endsOn: string;
  lifetime: boolean;
  terms: string;
}
export function warrantyProblems(f: WarrantyForm): string[] {
  const out: string[] = [];
  const date = /^\d{4}-\d{2}-\d{2}$/;
  if (!date.test(f.startsOn)) out.push('Pick the day it starts.');
  if (!f.lifetime && !date.test(f.endsOn)) out.push('Pick the day it ends, or tick lifetime.');
  if (!f.lifetime && date.test(f.startsOn) && date.test(f.endsOn) && f.endsOn < f.startsOn)
    out.push('It can’t end before it starts.');
  return out;
}
export const warrantyInput = (equipmentId: string, warrantyId: string, f: WarrantyForm) => ({
  warrantyId,
  equipmentId,
  kind: f.kind,
  startsOn: f.startsOn,
  endsOn: f.lifetime ? null : f.endsOn,
  ...(f.terms.trim() ? { terms: f.terms.trim() } : {}),
});
